/**
 * A3 and A4 — the queue page and the processing states, at the stable URL the
 * copy promises you can come back to.
 *
 * Every user-visible string here is cloned from a server-rendered template.
 * The three stage lines ("Downloading audio…", "Transcribing… ({1:20} of
 * {3:45})", "Tidying up…") live in `states.html` as `[data-stage-label]`
 * elements, and this module picks one by the machine `stage` value from the
 * API. Picking copy by prose would mean the copy could not change without
 * changing this file.
 */

import type { ApiError } from '../lib/api.ts';
import type { JobResponse } from '../lib/api-types.ts';
import { withAutoAction } from '../lib/auto-action.ts';
import { data, must, qs, qsa, setData } from '../lib/dom.ts';
import { downloadFrom, withDownload } from '../lib/download-pref.ts';
import { track } from '../lib/events.ts';
import { clock, minutesCost } from '../lib/format.ts';
import { pollJob } from '../lib/poll.ts';
import { countTranslateDone, withAsked } from '../lib/translate-target.ts';
import {
  minuteCount,
  podcastVariant,
  renderState,
  showState,
  UPLOAD_FLAG,
  uploadVariant,
} from '../lib/states.ts';

export function mountJobPage(): void {
  const root = qs<HTMLElement>('[data-job]');
  if (!root) return;
  const jobId = data(root, 'job-id');
  if (!jobId) return;

  const region = must<HTMLElement>('#banner-region');
  // E5, E6 and E12 have upload wordings: a file is not a video and has no
  // platform to blame. The server marks the card and ships those templates
  // only on an upload's page. A podcast's page ships E5's and E6's, because an
  // episode is a recording too, and E12's podcast wording, because "try the
  // file again" is not advice for a feed link. The variant functions take
  // whichever the page banked.
  const kind = data(root, 'source-kind');
  const copyFor = (state: string): string =>
    kind === 'podcast' ? podcastVariant(state) : kind === 'upload' ? uploadVariant(state) : state;
  // And a line that is about a link, E7's podcast advice, drops on an upload.
  const flags = kind === 'upload' ? UPLOAD_FLAG : {};
  const statusHost = must<HTMLElement>('[data-job-status]', root);
  const progress = qs<HTMLElement>('[data-job-progress]', root);
  const stageBank = must<HTMLElement>('[data-stage-labels]', root);
  const stepper = qs<HTMLElement>('[data-job-steps]', root);
  const headings = qsa<HTMLElement>('[data-job-heading]', root);

  /**
   * Mark the stepper from the machine `stage`: every step before it done, it
   * active, the rest pending. The labels are server-rendered; this only moves
   * attributes. A stage the list does not know (null while queued) marks
   * nothing active rather than guessing.
   */
  function paintSteps(stage: string | null): void {
    if (!stepper) return;
    const steps = qsa<HTMLElement>('[data-step]', stepper);
    const current = steps.findIndex((step) => data(step, 'step') === stage);
    steps.forEach((step, i) => {
      const state = current < 0 || i > current ? 'pending' : i < current ? 'done' : 'active';
      setData(step, 'step-state', state);
      if (state === 'active') step.setAttribute('aria-current', 'step');
      else step.removeAttribute('aria-current');
    });
  }

  /**
   * Swap "Working on your transcript" for the failure heading. Both are in the
   * template; this only moves `hidden`. A page from before the second heading
   * existed has nothing to swap to and keeps the first.
   */
  function headFailed(): void {
    if (!headings.some((h) => data(h, 'job-heading') === 'failed')) return;
    for (const heading of headings) heading.hidden = data(heading, 'job-heading') !== 'failed';
  }

  /** Pull one stage line out of the server-rendered bank and fill its slots. */
  function stageLine(stage: string, done: string, total: string): Node | null {
    const source = qs<HTMLElement>(`[data-stage-label="${stage}"]`, stageBank);
    if (!source) return null;
    const node = source.cloneNode(true) as HTMLElement;
    const doneSlot = qs<HTMLElement>('[data-slot="done"]', node);
    const totalSlot = qs<HTMLElement>('[data-slot="total"]', node);
    if (doneSlot) doneSlot.textContent = done;
    if (totalSlot) totalSlot.textContent = total;
    return node;
  }

  function paintQueued(job: JobResponse): void {
    const position = (job.queue_position ?? 0) + 1;
    // No eta drops the clause; one minute reads "roughly a minute". An eta of
    // zero is still a wait, so it counts as one.
    const wait =
      job.eta_s === null || job.eta_s === undefined ? {} : minuteCount(minutesCost(job.eta_s));
    const frag = renderState('A3', { position: String(position), ...wait });
    if (frag) statusHost.replaceChildren(frag);
    paintSteps(null);
    if (progress) {
      setData(progress, 'indeterminate', 'true');
      progress.hidden = false;
    }
  }

  function paintRunning(job: JobResponse): void {
    const stage = job.stage ?? 'queued';
    const done = job.progress ? clock(job.progress.done_s) : '';
    const total = job.progress ? clock(job.progress.total_s) : '';
    const line = stageLine(stage, done, total);
    paintSteps(job.stage ?? null);
    if (line) {
      const wrapper = document.createElement('p');
      wrapper.className = 'status-line';
      wrapper.append(line);
      statusHost.replaceChildren(wrapper);
    }
    if (progress) {
      progress.hidden = false;
      if (job.progress && job.progress.total_s > 0) {
        setData(progress, 'indeterminate', null);
        const pct = Math.max(0, Math.min(100, (job.progress.done_s / job.progress.total_s) * 100));
        progress.style.setProperty('--progress-pct', `${pct.toFixed(1)}%`);
      } else {
        setData(progress, 'indeterminate', 'true');
      }
    }
  }

  const handle = pollJob(jobId, {
    onUpdate(job) {
      if (job.status === 'queued') paintQueued(job);
      else if (job.status === 'running') paintRunning(job);
    },
    onTerminal(job) {
      if (progress) progress.hidden = true;
      if (job.status === 'done' && job.translate) {
        // A translation is ready (SEO_BRIEF_09 2.7), counted as its poll says it was made.
        countTranslateDone(jobId, job.translate);
      }
      if (job.status === 'done' && job.transcript_url) {
        // `?download=`, when an audio tool page sent the visitor here with one,
        // goes on to the transcript, which puts that format first. So does a
        // summarizer's or the caption generator's `?do=` (lib/auto-action.ts),
        // which the transcript page then runs once.
        // A translation asked for (`job.translate`) goes on as `?asked=`, so the transcript
        // can say it was already in that language (lib/translate-target.ts withAsked).
        location.assign(
          withAutoAction(
            withDownload(
              withAsked(job.transcript_url, job.translate),
              downloadFrom(location.search),
            ),
            location.search,
          ),
        );
        return;
      }
      statusHost.replaceChildren();
      // A stepper frozen on its last step, still pulsing, would say the job
      // is running beside a banner saying it is not. The banner is the state.
      if (stepper) stepper.hidden = true;
      headFailed();
      // Job-terminal states arrive as `state` (E6, E12, E18, E19…) on a 200.
      // The copy for each one asserts the refund, and `refunded: true` on the
      // wire is what makes that assertion checkable rather than hopeful.
      const state = job.state ?? job.error_code ?? 'E12';
      showState(region, copyFor(state), flags);
      // The wire's state, not the variant: per-state frequency counts E12
      // once, whichever wording it was shown in.
      track('error_state', { state });
    },
    onError(err: ApiError) {
      if (err.problem.status === 404) {
        if (stepper) stepper.hidden = true;
        headFailed();
        showState(region, copyFor('E12'), flags);
        return;
      }
      // Transient: polling keeps retrying with backoff, so nothing is shown
      // for a single failed poll. Only a 404 is terminal.
    },
  });

  // A retry button inside a job-terminal banner restarts the whole submission,
  // which means going back to where the URL was typed.
  region.addEventListener('click', (ev) => {
    const target = ev.target;
    if (target instanceof HTMLElement && data(target, 'action') === 'retry') {
      location.assign('/');
    }
  });

  addEventListener('pagehide', () => handle.stop());

  // Anything the server already rendered into the page stays until the first
  // poll answers, so the page is never blank while the request is in flight.
  for (const skeleton of qsa<HTMLElement>('[data-job-skeleton]', root)) skeleton.hidden = false;
}
